import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '@/theme/scalingTokens';

/**
 * Figma-matched styles for RequestCard
 * Based on: https://www.figma.com/design/vPvAacd1QiZGAj2OLTKzmT/GWS---Event-Management-tool?node-id=206-9589
 *
 * Figma 2026 variant (Employee module): node `1-1498` — white card with
 * neutral border, 8 px radius. Opt-in via `figmaVariant` prop.
 */
export const useRequestCardStyles = makeStyles({
  card: {
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('16px'),
    ...shorthands.padding('28px', '32px'),
    boxShadow: tokens.shadow4,
    display: 'flex',
    ...shorthands.gap('24px'),
    alignItems: 'flex-start',
    cursor: 'pointer',
    ':hover': {
      boxShadow: tokens.shadow8,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandForeground1,
      outlineOffset: '2px',
    },
    [mq.tablet]: {
      ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
      ...shorthands.gap(tokens.spacingHorizontalM),
    },
    [mq.mobile]: {
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
      ...shorthands.gap(tokens.spacingHorizontalS),
      ...shorthands.borderRadius('12px'),
    },
  },
  /**
   * Figma 2026 RequestCard look: white surface with 1 px neutral border,
   * 8 px radius, subtler shadow that lifts only on hover. Opt-in via
   * `figmaVariant=true`.
   */
  cardFigma: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('0.5rem'),
    boxShadow: 'none',
    ':hover': {
      boxShadow: tokens.shadow4,
    },
  },
  draftCard: {},
  // spec 104 US3: contentArea is a 3-row grid. Row 1 holds title + status,
  // row 2 holds the info items full-width, row 3 holds the metadata (left)
  // and a unified actionsGroup (right) so the primary + secondary buttons
  // sit on the same row as the request id. Keeping both buttons on one row
  // avoids the right-edge jitter that came from buttons of differing widths
  // stacking on adjacent rows. At <=639 px we collapse to a single column.
  contentArea: {
    flexGrow: 1,
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gridTemplateRows: 'auto auto auto',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalS,
    alignItems: 'start',
    [mq.mobile]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'repeat(4, auto)',
    },
  },
  headerRow: {
    gridColumn: '1 / -1',
    gridRow: '1',
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalM),
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
  },
  title: {
    minWidth: 0,
    fontWeight: tokens.fontWeightBold,
    fontSize: fontScale.subtitle,
    lineHeight: '1.3',
    color: tokens.colorNeutralForeground1,
  },
  infoRow: {
    gridColumn: '1 / -1',
    gridRow: '2',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    marginBottom: '12px',
    flexWrap: 'wrap',
    [mq.tablet]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    [mq.mobile]: {
      gridColumn: '1',
      gridRow: '2',
    },
  },
  // Drafts keep their actions group on row 2 (no row-3 metadata), so the
  // info row must stay in column 1 only — otherwise the full-width info row
  // overlaps the "Continue editing"/"Delete" buttons sitting in column 2.
  infoRowDraft: {
    gridColumn: '1',
    [mq.mobile]: {
      gridColumn: '1',
    },
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalXS, '0'),
    marginBottom: tokens.spacingVerticalXS,
  },
  infoItemDraftStep: {
    flex: '0 0 50%',
    paddingRight: tokens.spacingHorizontalXL,
    minWidth: '0',
  },
  infoItemDraftSaved: {
    flex: '0 0 30%',
    minWidth: '0',
  },
  infoItemLocation: {
    flex: '0 0 50%',
    minWidth: '0',
    paddingRight: tokens.spacingHorizontalXL,
    overflow: 'hidden',
  },
  infoItemDate: {
    flex: '0 0 30%',
    whiteSpace: 'nowrap',
  },
  infoItemTime: {
    flex: '0 0 auto',
    whiteSpace: 'nowrap',
    paddingRight: tokens.spacingHorizontalXL,
  },
  infoItemReason: {
    flex: '1 0 100%',
  },
  reasonText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '40ch',
  },
  reasonLabel: {
    fontWeight: tokens.fontWeightSemibold,
  },
  moreSpaces: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.padding('1px', tokens.spacingHorizontalXS),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    backgroundColor: tokens.colorNeutralBackground5,
    color: tokens.colorNeutralForeground2,
    fontSize: fontScale.caption,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'nowrap',
    marginLeft: tokens.spacingVerticalXS,
  },
  infoText: {
    fontSize: fontScale.body,
    lineHeight: '1.4',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  wrappingText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  // spec 104 US4 (FR-013, FR-014): info-row icons render at the Fluent
  // 20-size variant and use the brand-blue foreground token so they stand
  // out at a glance. The 20px font-size is a permitted fixed pixel value
  // per constitution XXII (icon sizes are an explicit exception). Drafts
  // and submitted cards share the same icon treatment for visual parity.
  icon: {
    fontSize: '20px',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  secondaryRow: {
    gridColumn: '1',
    gridRow: '3',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  secondaryText: {
    minWidth: 0,
    fontSize: fontScale.body,
    lineHeight: '1.4',
    color: tokens.colorNeutralForeground2,
  },
  // spec 104 US3 (FR-009, FR-010): row-3 right cell containing BOTH the
  // primary CTA and the state-appropriate secondary action side-by-side as
  // separate columns. Anchoring both buttons to the same row keeps their
  // widths from causing the visual jitter we saw when they sat on adjacent
  // rows. On narrow viewports (<=639 px) the buttons stack full-width with
  // the primary on top.
  actionsGroup: {
    gridColumn: '2',
    gridRow: '3',
    justifySelf: 'end',
    alignSelf: 'center',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    [mq.mobile]: {
      gridColumn: '1',
      gridRow: '4',
      justifySelf: 'stretch',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  // Draft variant: drafts have no row 3 metadata, so the actions group sits
  // on row 2 next to the step + saved-date info items rather than hanging
  // in an empty row 3.
  draftActionsGroup: {
    gridRow: '2',
    [mq.mobile]: {
      gridRow: '3',
    },
  },
  actionButton: {
    minWidth: 'unset',
    width: 'fit-content',
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    [mq.mobile]: {
      width: '100%',
    },
  },
  secondaryActionButton: {
    minWidth: 'unset',
    width: 'fit-content',
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    [mq.mobile]: {
      width: '100%',
    },
  },
});
